import { dataLayerPush } from '@libs/analytics';

import type { ProfileCompletionReturnTo, ProfileCompletionStepId } from './constants';
import { safeGet, safeRemove, safeSet } from './safe-session-storage';

export const START_PROFILE_STEPPER_EVENT_KEY = 'profileCompleteStartStepperEvent';
export const COMPLETE_PROFILE_EVENT_CONTEXT_KEY = 'profileCompleteEventContext';

const ENTRY_POINT_BY_STEP = {
    pet: 'pet',
    address: 'address',
    notifications: 'opt-in',
} as const;

type StartProfileStepperEntryPoint = (typeof ENTRY_POINT_BY_STEP)[ProfileCompletionStepId];

type StartProfileStepperEvent = {
    event: 'start_profile_stepper';
    site_section: 'treats rewards' | 'about you';
    entry_point: StartProfileStepperEntryPoint;
};

type ProfileStepperEventContext = Omit<StartProfileStepperEvent, 'event'>;

function isStartProfileStepperEvent(value: unknown): value is StartProfileStepperEvent {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const event = value as StartProfileStepperEvent;
    return (
        event.event === 'start_profile_stepper' &&
        (event.site_section === 'treats rewards' || event.site_section === 'about you') &&
        (event.entry_point === 'pet' || event.entry_point === 'address' || event.entry_point === 'opt-in')
    );
}

function isProfileStepperEventContext(value: unknown): value is ProfileStepperEventContext {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const context = value as ProfileStepperEventContext;
    return (
        (context.site_section === 'treats rewards' || context.site_section === 'about you') &&
        (context.entry_point === 'pet' || context.entry_point === 'address' || context.entry_point === 'opt-in')
    );
}

export function stageStartProfileStepperEvent(
    returnTo: ProfileCompletionReturnTo,
    step: ProfileCompletionStepId
): void {
    const event: StartProfileStepperEvent = {
        event: 'start_profile_stepper',
        site_section: returnTo === 'treats' ? 'treats rewards' : 'about you',
        entry_point: ENTRY_POINT_BY_STEP[step],
    };
    safeSet(START_PROFILE_STEPPER_EVENT_KEY, JSON.stringify(event));
    safeSet(
        COMPLETE_PROFILE_EVENT_CONTEXT_KEY,
        JSON.stringify({ site_section: event.site_section, entry_point: event.entry_point })
    );
}

export function clearStagedStartProfileStepperEvent(): void {
    safeRemove(START_PROFILE_STEPPER_EVENT_KEY);
}

export function clearCompleteProfileEventContext(): void {
    safeRemove(COMPLETE_PROFILE_EVENT_CONTEXT_KEY);
}

export function publishStagedStartProfileStepperEvent(): void {
    const raw = safeGet(START_PROFILE_STEPPER_EVENT_KEY);
    if (!raw) {
        return;
    }

    clearStagedStartProfileStepperEvent();

    try {
        const event: unknown = JSON.parse(raw);
        if (!isStartProfileStepperEvent(event)) {
            return;
        }
        dataLayerPush(event);
    } catch {
        // Ignore malformed payloads left in sessionStorage.
    }
}

export function publishCompleteProfileEvent(): void {
    const raw = safeGet(COMPLETE_PROFILE_EVENT_CONTEXT_KEY);
    if (!raw) {
        return;
    }

    safeRemove(COMPLETE_PROFILE_EVENT_CONTEXT_KEY);

    try {
        const context: unknown = JSON.parse(raw);
        if (!isProfileStepperEventContext(context)) {
            return;
        }
        dataLayerPush({ event: 'complete_profile', ...context });
    } catch {
        // Ignore malformed payloads left in sessionStorage.
    }
}
